import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useDisciplineStore } from '../../contexts/disciplineStore';
import CreateDisciplineGoalModal from './CreateDisciplineGoalModal';
import ImprovedTimerSlider from './ImprovedTimerSlider';
import { 
  Clock, Target, Plus, BookOpen, Star, 
  Heart, Brain, Timer, PlayCircle, CheckCircle, 
  Zap, Sparkles, Flame, TrendingUp, Home, ArrowLeft,
  Trophy, BarChart2, X, Activity, PieChart, Gauge, Layers, ChevronRight
} from 'lucide-react';
import { computeDisciplineMetrics, buildSparkline } from '../../utils/disciplineMetrics';
import { useUI } from '../../contexts/UIContext'; // Import useUI

/**
 * Modern, User-Friendly Digital Discipline Panel
 * 
 * Enhanced Features:
 * - Warm, inviting gradient backgrounds with soft shadows
 * - Micro-interactions and satisfying animations 
 * - Intuitive card-based navigation with hover effects
 * - Encouraging progress visualization with celebrations
 * - Mobile-first responsive design with touch-friendly controls
 * - Real-time updates optimized for smooth 60fps experience
 * - Gamification elements that feel rewarding, not overwhelming
 */

const ModernDisciplinePanel = ({ isDarkMode = false, navigateToScreen }) => {
  const { t } = useTranslation('digitalDiscipline');
  const { screenParams } = useUI(); // Get screenParams from context

  // ... (props logging)

  // Store hooks
  const {
    goals = [],
    timers = [],
    xp = 0,
    streak = 0,
    completedGoals = [],
    addGoal,
    startTimer,
    initializeSampleData,
    normalizeDurations,
    heuristicSuggestNextGoal
  } = useDisciplineStore();

  const [activeView, setActiveView] = useState('dashboard');
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [notification, setNotification] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [celebrationActive, setCelebrationActive] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [creatingTimers, setCreatingTimers] = useState(new Set());
  const processedAutoQuestRef = useRef(null); // Ref to prevent double processing

  // Deep Linking & Auto-Quest Logic
  useEffect(() => {
    if (!screenParams) return;

    // 1. Handle Tab Switching
    if (screenParams.tab && ['dashboard', 'goals', 'timers', 'progress'].includes(screenParams.tab)) {
      setActiveView(screenParams.tab);
    }

    // 2. Handle Auto-Quest (Deep Link from TodayScreen)
    const handleAutoQuest = async () => {
      const { autoQuest } = screenParams;
      if (autoQuest && autoQuest.title && processedAutoQuestRef.current !== autoQuest.title) {
        console.log('🚀 Auto-Quest Triggered:', autoQuest);
        processedAutoQuestRef.current = autoQuest.title; // Mark processed

        try {
          // Construct new goal object
          const newGoal = {
            id: `quest-${Date.now()}`, // Unique ID
            name: autoQuest.title,
            category: autoQuest.category || 'productivity',
            duration: autoQuest.duration || 25,
            xpReward: 50, // Standard reward
            created: new Date().toISOString(),
            completed: false,
            color: 'blue' // Default color
          };

          // create goal
          await addGoal(newGoal);
          
          // Switch to timers view
          setActiveView('timers'); // Force view switch
          
          // Start timer for this goal immediately
          setTimeout(async () => {
             await startTimer?.(newGoal.id);
          }, 500);

          // Show specific notification instead of generic one
          setNotification({
             message: t('digitalDiscipline.notifications.questStarted', 'Daily Quest Started: {name} ⚔️', { name: newGoal.name }),
             type: 'success',
             id: Date.now()
          });

        } catch (error) {
          console.error('Auto-Quest Failed:', error);
          setNotification({ message: 'Failed to start daily quest.', type: 'error', id: Date.now() });
        }
      }
    };

    handleAutoQuest();

  }, [screenParams, addGoal, startTimer, t]);

  // Warm, modern theme system

  // Warm, modern theme system
  const theme = {
    // Background: calmer, more neutral with subtle blue/teal cues
    background: isDarkMode
      ? 'bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950'
      : 'bg-gradient-to-br from-slate-50 via-white to-slate-50',

    // Card: glassy neutrals with restrained shadows
    card: isDarkMode
      ? 'bg-slate-800/60 backdrop-blur-xl border border-slate-700/40 shadow-lg shadow-black/30'
      : 'bg-white/70 backdrop-blur-xl border border-slate-200/60 shadow-lg shadow-emerald-500/5',

    // Buttons: blue/indigo primary gradient with soft hover (Consistent with Global Blue Theme)
    button: isDarkMode
      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 shadow-lg shadow-blue-500/25'
      : 'bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-400 hover:to-indigo-400 shadow-lg shadow-blue-500/20',

    // Accent text
    accent: isDarkMode ? 'text-blue-400' : 'text-blue-600',

    // Text colors
    text: isDarkMode ? 'text-slate-100' : 'text-slate-800',
    textSecondary: isDarkMode ? 'text-slate-400' : 'text-slate-600',
  };

  // Calculate comprehensive metrics
  const stats = useMemo(() => {
    const safeGoals = Array.isArray(goals) ? goals : [];
    const safeTimers = Array.isArray(timers) ? timers : [];

    // Filter out Turkish/duplicate timers
    const filteredTimers = safeTimers.filter(t =>
      !t?.name?.includes('Türkçe') &&
      !t?.goalName?.includes('Türkçe')
    );

    const completedCount = safeGoals.filter(g => g.completed).length;
    const activeTimersCount = filteredTimers.filter(t =>
      t?.isActive && !t?.isCompleted && !t?.isCancelled
    ).length;

    const totalFocusMinutes = filteredTimers
      .filter(t => t?.isCompleted)
      .reduce((total, timer) => total + (timer.duration || 0), 0);

    const todayGoals = safeGoals.filter(g => {
      const today = new Date().toDateString();
      const goalDate = new Date(g.created).toDateString();
      return goalDate === today;
    }).length;

    return {
      totalGoals: safeGoals.length,
      completedGoals: completedCount,
      activeGoals: safeGoals.length - completedCount,
      activeTimers: activeTimersCount,
      completionRate: safeGoals.length > 0 ? Math.round((completedCount / safeGoals.length) * 100) : 0,
      totalFocusHours: Math.round(totalFocusMinutes / 60 * 10) / 10,
      todayGoals,
      currentStreak: streak,
      totalXP: xp
    };
  }, [goals, timers, streak, xp]);

  // Advanced metrics (memoized)
  const advanced = useMemo(() => computeDisciplineMetrics({ goals, timers, xp, streak, now: Date.now() }), [goals, timers, xp, streak]);
  const sparkPoints = useMemo(() => buildSparkline(advanced.weeklyFocus || []), [advanced.weeklyFocus]);

  // Initialize data and smooth loading
  useEffect(() => {
    const initializeData = async () => {
      try {
        if (goals.length === 0) {
          await initializeSampleData?.();
        }
  // One-time duration normalization & suggestion prefetch
  try { normalizeDurations?.(); } catch(e) {}
  try { heuristicSuggestNextGoal?.(); } catch(e) {}
        
        // Smooth loading transition
        setTimeout(() => setIsLoading(false), 300);
      } catch (error) {
        console.error('Initialization error:', error);
        setIsLoading(false);
      }
    };
    
    initializeData();
  }, [goals.length, initializeSampleData]);

  // Elegant notification system
  const showNotification = useCallback((message, type = 'info', duration = 4000) => {
    setNotification({ message, type, id: Date.now() });
    setTimeout(() => setNotification(null), duration);
  }, []);

  // Goal creation with celebrations
  const handleCreateGoal = useCallback(async (goalData) => {
    try {
      const newGoal = {
        id: Date.now().toString(),
        name: goalData.name || t('defaults.goalName', 'New Focus Goal'),
        category: goalData.category || 'productivity',
        duration: goalData.duration || 25,
        xpReward: goalData.xpReward || 50,
        created: new Date().toISOString(),
        completed: false,
        color: goalData.color || 'blue'
      };
      
      await addGoal?.(newGoal);
      setShowGoalModal(false);
      
      // Celebration animation
      setCelebrationActive(true);
      setTimeout(() => setCelebrationActive(false), 2000);
      
      showNotification(
        t('messages.goalCreated', 'Amazing! Your new goal is ready 🎯'), 
        'success'
      );
    } catch (error) {
      console.error('Goal creation error:', error);
      showNotification(
        t('errors.goalCreation', 'Oops! Something went wrong. Please try again.'), 
        'error'    );
    }
  }, [addGoal, showNotification, t]);

  // Enhanced timer starting with feedback and debounce protection
  const handleStartTimer = useCallback(async (goal) => {
    try {
      if (!goal?.id) {
        showNotification(t('errors.invalidGoal', 'Please select a valid goal'), 'warning');
        return;
      }

      // Prevent double-clicking/rapid button presses
      if (creatingTimers.has(goal.id)) {
        console.log('Timer creation already in progress for goal:', goal.id);
        return;
      }

      // Add to creating set
      setCreatingTimers(prev => new Set([...prev, goal.id]));

      // Filter out Turkish/duplicate timers
      const activeTimers = timers.filter(t => 
        t?.isActive && !t?.isCompleted && !t?.isCancelled &&
        !t?.name?.includes('Türkçe') && !t?.goalName?.includes('Türkçe')
      );

      if (activeTimers.length >= 5) {
        showNotification(
          t('digitalDiscipline.notifications.timerLimitReached', 'You can have up to 5 active timers. Complete one first! ⏰'), 
          'warning'
        );
        // Remove from creating set
        setCreatingTimers(prev => {
          const newSet = new Set(prev);
          newSet.delete(goal.id);
          return newSet;
        });
        return;
      }

      const hasActiveTimer = activeTimers.some(timer => timer.goalId === goal.id);
      if (hasActiveTimer) {
        showNotification(
          t('digitalDiscipline.notifications.goalNotFound', 'This goal already has an active timer running! 🏃‍♂️'), 
          'info'
        );
        setActiveView('timers');
        // Remove from creating set
        setCreatingTimers(prev => {
          const newSet = new Set(prev);
          newSet.delete(goal.id);
          return newSet;
        });
        return;
      }

  const result = await startTimer?.(goal.id);

  // The store's startTimer returns the new timerId (string) on success, not an object
  const isSuccess = typeof result === 'string' && result.length > 0;
  if (isSuccess) {
        setActiveView('timers');
        showNotification(
          t('digitalDiscipline.notifications.timerStarted', 'Timer started! Focus time begins now 🚀'), 
          'success'
        );
        
        // Smooth transition to timer view
        setTimeout(() => {
          const timerElement = document.getElementById('active-timers');
          if (timerElement) {
            timerElement.scrollIntoView({ behavior: 'smooth' });
          }
        }, 300);
    } else {
        showNotification(
      (result && result.message) ? result.message : t('errors.timerStart', 'Failed to start timer'), 
          'error'
        );
      }
    } catch (error) {
      console.error('Timer start error:', error);
      showNotification(t('errors.timerStart', 'Something went wrong. Try again!'), 'error');
    } finally {
      // Always remove from creating set after operation completes
      setTimeout(() => {
        setCreatingTimers(prev => {
          const newSet = new Set(prev);
          newSet.delete(goal.id);
          return newSet;
        });
      }, 1000); // 1 second debounce
    }
  }, [timers, startTimer, showNotification, t, creatingTimers]);

  // Navigation items with beautiful icons
  // Memoize navigation to prevent re-renders on ticking stats (timers interval etc.)
  const navigationItems = useMemo(() => ([
    {
      id: 'dashboard',
      name: t('digitalDiscipline.nav.dashboard', 'Dashboard'),
      icon: Home,
      color: 'blue',
      description: t('digitalDiscipline.nav.dashboardDesc', 'Your personal growth overview')
    },
    {
      id: 'goals',
      name: t('digitalDiscipline.nav.goals', 'My Goals'),
      icon: Target,
      color: 'green',
      description: t('digitalDiscipline.nav.goalsDesc', 'Track and achieve your goals'),
      badge: stats.activeGoals
    },
    {
      id: 'timers',
      name: t('digitalDiscipline.nav.timers', 'Focus Sessions'),
      icon: Timer,
      color: 'orange',
      description: t('digitalDiscipline.nav.timersDesc', 'Active focus sessions'),
      badge: stats.activeTimers
    },
    {
      id: 'progress',
      name: t('digitalDiscipline.nav.progress', 'Progress'),
      icon: TrendingUp,
      color: 'purple',
      description: t('digitalDiscipline.nav.progressDesc', 'Your achievements and stats')
    }
  ]), [t, stats.activeGoals, stats.activeTimers]);

  // Quick action cards
  // New streamlined quick actions focusing on multi-domain wellbeing + goal creation
  const quickActions = useMemo(() => ([
    // Consolidated Goal Creator
    {
      id: 'create-goal',
      name: t('digitalDiscipline.quick.createGoal', 'Create Goal'),
      description: t('digitalDiscipline.quick.createGoalDesc', 'Custom focus / habit goal'),
      icon: Plus,
      color: 'blue',
      action: () => setShowGoalModal(true)
    },
    // Mental boost (logs wellbeing event + optional micro goal)
    {
      id: 'mental-boost',
      name: t('digitalDiscipline.quick.mentalBoost', 'Mental Boost'),
      description: t('digitalDiscipline.quick.mentalBoostDesc', '2-min breathing / mindfulness'),
      icon: Brain,
      color: 'purple',
      action: () => {
        // Lightweight wellbeing event
        try { useDisciplineStore.getState().logMentalActivity({ minutes: 2, type: 'breathing' }); } catch(e) {}
        showNotification(t('digitalDiscipline.notifications.mentalLogged','Mental activity logged 🧠'), 'success');
      }
    },
    // Physical micro break
    {
      id: 'physical-break',
      name: t('digitalDiscipline.quick.physicalBreak', 'Micro Stretch'),
      description: t('digitalDiscipline.quick.physicalBreakDesc', '2-min posture & stretch'),
      icon: Activity,
      color: 'green',
      action: () => {
        try { useDisciplineStore.getState().logPhysicalActivity({ minutes: 2, type: 'stretch' }); } catch(e) {}
        showNotification(t('digitalDiscipline.notifications.physicalLogged','Physical activity logged 🏃'), 'success');
      }
    },
    // Social check-in
    {
      id: 'social-check',
      name: t('digitalDiscipline.quick.socialCheck', 'Social Check-in'),
      description: t('digitalDiscipline.quick.socialCheckDesc', 'Send a positive msg'),
      icon: Heart,
      color: 'red',
      action: () => {
        try { useDisciplineStore.getState().logSocialActivity({ minutes: 1, type: 'checkin' }); } catch(e) {}
        showNotification(t('digitalDiscipline.notifications.socialLogged','Social connection logged ❤️'), 'success');
      }
    },
    // Digital detox micro block
    {
      id: 'digital-detox',
      name: t('digitalDiscipline.quick.digitalDetox', 'Digital Detox'),
      description: t('digitalDiscipline.quick.digitalDetoxDesc', '15-min no distraction'),
      icon: Timer,
      color: 'orange',
      action: () => {
        const detoxGoal = { name: t('digitalDiscipline.quick.digitalDetoxGoal','Digital Detox Block'), category: 'digital', duration: 15 };
        handleCreateGoal(detoxGoal);
      }
    },
    // Focus Trivia (prototype: instant XP + wellbeing event)
    {
      id: 'focus-trivia',
      name: t('digitalDiscipline.quick.focusTrivia', 'Focus Trivia'),
      description: t('digitalDiscipline.quick.focusTriviaDesc', 'Answer a quick brain teaser'),
      icon: Sparkles,
      color: 'indigo',
      action: () => {
        // Simple random micro-question placeholder
        const questions = [
          t('digitalDiscipline.trivia.q1','Deep work ideal block? (25 / 45 / 90)'),
          t('digitalDiscipline.trivia.q2','Best posture reminder interval? (20 / 60 / 120)'),
          t('digitalDiscipline.trivia.q3','Daily mindfulness target (5 / 10 / 30) mins?')
        ];
        const q = questions[Math.floor(Math.random()*questions.length)];
        const answer = window.prompt(q,'');
        // Log as mental activity regardless for now
        try { useDisciplineStore.getState().logMentalActivity({ minutes: 1, type: 'trivia' }); } catch(e) {}
        showNotification(t('digitalDiscipline.notifications.triviaLogged','Trivia logged ✨'), 'info');
      }
    }
  ]), [t, handleCreateGoal, showNotification]);

  // Loading screen with smooth animation
  if (isLoading) {
    return (
      <div className={`min-h-screen ${theme.background} flex items-center justify-center`}>
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className={`w-16 h-16 border-4 border-transparent ${theme.accent} border-t-current rounded-full mx-auto mb-4`}
          />
          <h3 className={`text-xl font-semibold ${theme.text} mb-2`}>
            {t('loading.title', 'Setting up your workspace')}
          </h3>
          <p className={theme.textSecondary}>
            {t('loading.subtitle', 'Preparing your personalized experience...')}
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme.background} ${theme.text}`}
      style={{
        WebkitOverflowScrolling: 'touch',
        overscrollBehavior: 'contain'
      }}
    >
      {/* Enhanced Header with Stats */}
      <motion.header 
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className={`sticky top-0 z-40 backdrop-blur-xl ${isDarkMode ? 'bg-slate-900/50 border-slate-700/40' : 'bg-white/60 border-slate-200/60'} border-b`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Back Button */}              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  console.log('🔥 Back button clicked! navigateToScreen:', typeof navigateToScreen);
                  console.log('🔥 Navigating to dashboard...');
                  if (navigateToScreen) {
                    navigateToScreen('dashboard');
                  } else {
                    console.error('❌ navigateToScreen function is not available!');
                  }
                }}
                className={`${theme.card} p-3 rounded-xl hover:shadow-xl transition-all duration-200`}
                title={t('navigation.back', 'Back to Dashboard')}
              >
                <ArrowLeft className="w-5 h-5" />
              </motion.button>
              
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-500 to-indigo-500 bg-clip-text text-transparent">
                  {t('digitalDiscipline.panelTitle', 'Digital Discipline Panel')}
                </h1>
                <p className={`text-sm ${theme.textSecondary} mt-1`}>
                  {t('digitalDiscipline.panelDescription', 'Control your digital habits, achieve your goals')}
                </p>
              </div>
            </div>
            
            {/* Live Stats Badge */}
            <motion.div 
              whileHover={{ scale: 1.05 }}
              className={`${theme.card} rounded-2xl px-4 py-2 cursor-pointer`}
              onClick={() => setActiveView('progress')}
            >
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-1">
                  <Flame className="w-4 h-4 text-amber-500" />
                  <span className="text-sm font-medium">{stats.currentStreak}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Star className="w-4 h-4 text-yellow-500" />
                  <span className="text-sm font-medium">{stats.totalXP}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Timer className="w-4 h-4 text-teal-500" />
                  <span className="text-sm font-medium">{stats.activeTimers}</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Navigation Tabs */}
        <motion.nav 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <div className="flex space-x-2 overflow-x-auto pb-2">
            {navigationItems.map((item) => (
              <motion.button
                key={item.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setActiveView(item.id)}
                className={`
                  flex-shrink-0 flex items-center space-x-3 px-6 py-3 rounded-2xl font-medium
                  transition-all duration-200 relative overflow-hidden
                  ${activeView === item.id 
                    ? `${theme.button} text-white shadow-lg` 
                    : `${theme.card} ${theme.text} hover:shadow-md`
                  }
                `}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.name}</span>
                {item.badge > 0 && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="bg-red-500 text-white text-xs px-2 py-1 rounded-full"
                  >
                    {item.badge}
                  </motion.span>
                )}
              </motion.button>
            ))}
          </div>
        </motion.nav>

        {/* Content Views */}
        <AnimatePresence mode="wait">
          {activeView === 'dashboard' && (
            <motion.div
              key="dashboard"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              {/* Welcome Card */}
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className={`${theme.card} rounded-3xl p-6 sm:p-8`}
              >
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold mb-2">
                      {t('welcome.title', 'Welcome back! 👋')}
                    </h2>
                    <p className={theme.textSecondary}>
                      {t('welcome.subtitle', 'Ready to make today productive?')}
                    </p>
                  </div>
                  {celebrationActive && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: [0, 1.2, 1] }}
                      transition={{ duration: 0.6 }}
                      className="text-4xl"
                    >
                      🎉
                    </motion.div>
                  )}
                </div>
                
                {/* Quick Stats Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="text-center p-4 rounded-2xl bg-gradient-to-br from-blue-500/15 to-cyan-600/15">
                    <div className="text-2xl font-bold text-blue-500 mb-1">
                      {stats.completionRate}%
                    </div>
                    <div className="text-sm text-blue-400">Success Rate</div>
                  </div>
                  <div className="text-center p-4 rounded-2xl bg-gradient-to-br from-indigo-500/15 to-violet-600/15">
                    <div className="text-2xl font-bold text-indigo-500 mb-1">
                      {stats.totalFocusHours}h
                    </div>
                    <div className="text-sm text-indigo-400">Focus Time</div>
                  </div>
                  <div className="text-center p-4 rounded-2xl bg-gradient-to-br from-indigo-500/15 to-cyan-600/15">
                    <div className="text-2xl font-bold text-indigo-300 mb-1">
                      {stats.currentStreak}
                    </div>
                    <div className="text-sm text-indigo-300">Day Streak</div>
                  </div>
                  <div className="text-center p-4 rounded-2xl bg-gradient-to-br from-amber-500/15 to-orange-600/15">
                    <div className="text-2xl font-bold text-amber-300 mb-1">
                      {stats.todayGoals}
                    </div>
                    <div className="text-sm text-amber-300">Today's Goals</div>
                  </div>
                </div>
              </motion.div>

              {/* Quick Actions */}
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
                className={`${theme.card} rounded-3xl p-6`}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">{t('quickActions.title', 'Quick Actions')}</h3>
                  <span className={`text-xs ${theme.textSecondary}`}>{t('quickActions.sub', 'Boost your momentum')}</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  {quickActions.map((qa, idx) => {
                    const Icon = qa.icon;
                    return (
                      <motion.button
                        key={qa.id}
                        whileHover={{ y: -3, scale: 1.01 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={qa.action}
                        className={`${theme.card} rounded-2xl p-4 text-left group`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 text-white shadow-md">
                            <Icon className="w-5 h-5" />
                          </div>
                          <div>
                            <div className="text-sm font-medium">{qa.name}</div>
                            <div className={`text-xs ${theme.textSecondary}`}>{qa.description}</div>
                          </div>
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              </motion.div>

              {/* Create Goal Section - Premium Design */}
              <div>
                <motion.button
                  whileHover={{ scale: 1.01, y: -2 }}
                  whileTap={{ scale: 0.99 }}
                  onClick={() => setShowGoalModal(true)}
                  className={`w-full p-6 rounded-2xl border-2 transition-all duration-300 relative overflow-hidden group ${
                    isDarkMode
                      ? 'bg-gradient-to-br from-blue-900/10 via-indigo-900/10 to-cyan-900/10 border-blue-500/20 hover:border-blue-400/40 hover:shadow-2xl hover:shadow-blue-500/10'
                      : 'bg-gradient-to-br from-blue-50 via-indigo-50 to-cyan-50 border-blue-200/50 hover:border-blue-300/70 hover:shadow-xl hover:shadow-blue-500/10'
                  }`}
                >
                  {/* Background glow effect */}
                  <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
                    isDarkMode
                      ? 'bg-gradient-to-br from-blue-500/10 via-indigo-500/10 to-cyan-500/10'
                      : 'bg-gradient-to-br from-blue-100/40 via-indigo-100/40 to-cyan-100/40'
                  }`} />
                  
                  {/* Content */}
                  <div className="relative z-10 flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      {/* Icon with animated glow */}
                      <div className={`p-4 rounded-2xl transition-all duration-300 group-hover:scale-110 ${
                        isDarkMode
                          ? 'bg-gradient-to-br from-blue-500 to-indigo-500 shadow-lg group-hover:shadow-blue-500/30'
                          : 'bg-gradient-to-br from-blue-500 to-indigo-500 shadow-lg group-hover:shadow-blue-500/40'
                      }`}>
                        <Target className="h-8 w-8 text-white" />
                      </div>
                      
                      {/* Text content */}
                      <div className="text-left">
                        <h3 className={`text-xl font-bold mb-1 transition-colors ${
                          isDarkMode ? 'text-white group-hover:text-blue-100' : 'text-gray-900 group-hover:text-blue-900'
                        }`}>
                          {t('goals.createGoal', 'Yeni Hedef Oluştur')}
                        </h3>
                        <p className={`text-sm transition-colors ${
                          isDarkMode ? 'text-gray-400 group-hover:text-blue-300' : 'text-gray-600 group-hover:text-blue-600'
                        }`}>
                          {t('goals.createGoalDesc', 'Kişisel gelişim yolculuğuna başla, hedeflerini belirle ve başarıya ulaş')}
                        </p>
                      </div>
                    </div>
                    
                    {/* Arrow indicator */}
                    <div className={`transition-all duration-300 group-hover:translate-x-1 ${
                      isDarkMode ? 'text-blue-400' : 'text-blue-600'
                    }`}>
                      <ChevronRight className="h-6 w-6" />
                    </div>
                  </div>
                  
                  {/* Sparkle effects */}
                  <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                    <Sparkles className={`h-5 w-5 ${isDarkMode ? 'text-blue-300' : 'text-blue-500'}`} />
                  </div>
                  <div className="absolute bottom-4 left-20 opacity-0 group-hover:opacity-100 transition-opacity duration-1000">
                    <Sparkles className={`h-3 w-3 ${isDarkMode ? 'text-indigo-300' : 'text-indigo-500'}`} />
                  </div>
                </motion.button>
              </div>
            </motion.div>
          )}

          {activeView === 'goals' && (
            <motion.div
              key="goals"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              {/* Goals Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold mb-2">
                    {t('digitalDiscipline.goals.myGoals', 'My Goals')} 🎯
                  </h2>
                  <p className={theme.textSecondary}>
                    {t('digitalDiscipline.goals.title', 'Track your progress and achieve your dreams')}
                  </p>
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowGoalModal(true)}
                  className={`${theme.button} text-white px-6 py-3 rounded-2xl font-medium flex items-center space-x-2`}
                >
                  <Plus className="w-5 h-5" />
                  <span>{t('digitalDiscipline.goals.createGoal', 'Create Goal')}</span>
                </motion.button>
              </div>

              {/* Goals Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {goals.length === 0 ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className={`${theme.card} rounded-2xl p-8 text-center col-span-full`}
                  >
                    <Target className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                    <h3 className="text-xl font-semibold mb-2">
                      {t('digitalDiscipline.goals.noGoalsYet', 'No goals yet')}
                    </h3>
                    <p className={`${theme.textSecondary} mb-6`}>
                      {t('digitalDiscipline.goals.addFirstGoal', 'Create your first goal to get started!')}
                    </p>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setShowGoalModal(true)}
                      className={`${theme.button} text-white px-6 py-3 rounded-2xl font-medium`}
                    >
                      {t('digitalDiscipline.goals.addNew', 'Create Your First Goal')}
                    </motion.button>
                  </motion.div>
                ) : (
                  goals.map((goal, index) => (
                    <motion.div
                      key={goal.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ scale: 1.02, y: -4 }}
                      className={`${theme.card} rounded-2xl p-6 cursor-pointer transition-all duration-200 relative overflow-hidden`}
                      onClick={() => setSelectedGoal(goal)}
                    >
                      {/* Goal Status Indicator */}
                      <div className={`absolute top-4 right-4 w-3 h-3 rounded-full ${
                        goal.completed ? 'bg-green-500' : 'bg-blue-500'
                      }`} />
                      
                      {/* Goal Category Icon */}
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br from-${goal.color || 'teal'}-500 to-${goal.color || 'emerald'}-600 flex items-center justify-center mb-4`}>
                        <Target className="w-6 h-6 text-white" />
                      </div>
                      
                      <h3 className="font-semibold mb-2 text-lg">{goal.name}</h3>
                      <p className={`text-sm ${theme.textSecondary} mb-4`}>
                        {goal.category} • {goal.duration} minutes
                      </p>
                      
                      {/* Goal Progress */}
                      <div className="flex items-center justify-between">
                        <span className={`text-sm ${theme.textSecondary}`}>
                          {goal.completed ? t('goals.completed', 'Completed') : t('goals.active', 'Active')}
                        </span>                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStartTimer(goal);
                          }}
                          disabled={goal.completed || creatingTimers.has(goal.id)}
                          className={`
                            w-10 h-10 rounded-full flex items-center justify-center
                            ${goal.completed 
                              ? 'bg-emerald-500 text-white' 
                              : creatingTimers.has(goal.id)
                                ? 'bg-gray-400 text-white opacity-75 cursor-not-allowed'
                                : `${theme.button} text-white hover:shadow-lg`
                            }
                          `}
                        >
                          {goal.completed ? (
                            <CheckCircle className="w-5 h-5" />
                          ) : creatingTimers.has(goal.id) ? (
                            <Timer className="w-5 h-5 animate-spin" />
                          ) : (
                            <PlayCircle className="w-5 h-5" />
                          )}
                        </motion.button>
                      </div>
                    </motion.div>
                  ))
                )}
              </div>
            </motion.div>
          )}

          {activeView === 'timers' && (
            <motion.div
              key="timers"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              id="active-timers"
              className="space-y-6"
            >
              <div>
                <h2 className="text-2xl font-bold mb-2">
                  {t('timers.title', 'Focus Sessions')} ⏱️
                </h2>
                <p className={theme.textSecondary}>
                  {t('timers.subtitle', 'Your active focus sessions and progress')}
                </p>
              </div>              {/* Active Timers - Single Unified Container */}
              <div className="space-y-4">
                {timers.filter(t => 
                  t?.isActive && !t?.isCompleted && !t?.isCancelled && 
                  !t?.name?.includes('Türkçe') && !t?.goalName?.includes('Türkçe')
                ).length === 0 ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className={`${theme.card} rounded-2xl p-8 text-center`}
                  >
                    <Timer className="w-16 h-16 mx-auto mb-4 text-teal-400/70" />
                    <h3 className="text-xl font-semibold mb-2">
                      {t('timers.empty.title', 'No active sessions')}
                    </h3>
                    <p className={`${theme.textSecondary} mb-6`}>
                      {t('timers.empty.subtitle', 'Start a focus session from your goals')}
                    </p>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setActiveView('goals')}
                      className={`${theme.button} text-white px-6 py-3 rounded-2xl font-medium`}
                    >
                      {t('timers.goToGoals', 'View My Goals')}
                    </motion.button>
                  </motion.div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className={`${theme.card} rounded-2xl overflow-hidden`}
                  >
                    <ImprovedTimerSlider 
                      isDarkMode={isDarkMode} 
                      onNotification={showNotification}
                    />
                  </motion.div>
                )}
              </div>
            </motion.div>
          )}

          {activeView === 'progress' && (
            <motion.div
              key="progress"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              <div>
                <h2 className="text-2xl font-bold mb-2 flex items-center gap-2">
                  <Activity className="w-6 h-6 text-blue-500" />
                  {t('progress.title', 'Your Progress')}
                </h2>
                <p className={theme.textSecondary}>
                  {t('progress.subtitle', 'Deep insights into your focus discipline')}
                </p>
              </div>

              {/* Top KPI Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { icon: Trophy, label: t('progress.completedGoals','Completed Goals'), value: advanced.completedGoals, accent: 'from-yellow-500/20 to-orange-500/20' },
                  { icon: Flame, label: t('progress.streak','Streak'), value: advanced.streak, accent: 'from-orange-500/20 to-red-500/20' },
                  { icon: Star, label: t('progress.xp','XP'), value: advanced.xp, accent: 'from-purple-500/20 to-pink-500/20' },
                  { icon: Gauge, label: t('progress.efficiencyScore','Efficiency Score'), value: advanced.efficiencyScore + '%', accent: 'from-green-500/20 to-emerald-500/20' }
                ].map((kpi, idx) => (
                  <motion.div key={idx} whileHover={{ y:-4 }} className={`${theme.card} rounded-2xl p-4 relative overflow-hidden`}> 
                    <div className={`absolute inset-0 bg-gradient-to-br ${kpi.accent} opacity-60`}></div>
                    <div className="relative flex items-center justify-between">
                      <kpi.icon className="w-6 h-6 text-blue-400" />
                      <span className="text-xl font-bold font-mono">{kpi.value}</span>
                    </div>
                    <div className={`mt-2 text-xs ${theme.textSecondary} font-medium tracking-wide`}>{kpi.label}</div>
                  </motion.div>
                ))}
              </div>

              {/* Progress & Level */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <motion.div whileHover={{ scale:1.01 }} className={`${theme.card} rounded-2xl p-6 flex flex-col gap-4`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold mb-1 flex items-center gap-2"><Gauge className="w-4 h-4 text-indigo-400" />{t('progress.levelProgress','Level Progress')}</h3>
                      <p className={`text-xs ${theme.textSecondary}`}>Lv. {advanced.currentLevel} • {advanced.xpIntoLevel}/1000 XP</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">{t('progress.xpVelocity','XP / Day')}:</p>
                      <p className="text-lg font-bold font-mono">{advanced.xpVelocityPerDay}</p>
                    </div>
                  </div>
                  <div className="h-3 rounded-full bg-gradient-to-r from-slate-700/40 to-slate-600/40 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all" style={{ width: `${(advanced.xpIntoLevel/10)}%` }} />
                  </div>
                  <div className="flex justify-between text-xs text-slate-400">
                    <span>{t('progress.toNext','To Next')}: {advanced.xpToNext}</span>
                    <span>{t('progress.projectedLevelUp','Level Up In')}: {advanced.projectedDaysToNextLevel ?? '∞'} {t('progress.days','days')}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-center mt-2">
                    <div>
                      <p className="text-sm font-mono font-semibold">{advanced.focusHours}</p>
                      <p className="text-[10px] uppercase tracking-wide text-slate-400">{t('progress.focusHours','Focus Hours')}</p>
                    </div>
                    <div>
                      <p className="text-sm font-mono font-semibold">{advanced.avgSessionMinutes}</p>
                      <p className="text-[10px] uppercase tracking-wide text-slate-400">{t('progress.avgSession','Avg Session')}</p>
                    </div>
                    <div>
                      <p className="text-sm font-mono font-semibold">{advanced.medianSessionMinutes}</p>
                      <p className="text-[10px] uppercase tracking-wide text-slate-400">{t('progress.medianSession','Median')}</p>
                    </div>
                  </div>
                </motion.div>

                {/* Weekly Focus Sparkline */}
                <motion.div whileHover={{ scale:1.01 }} className={`${theme.card} rounded-2xl p-6 flex flex-col gap-4`}>
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold flex items-center gap-2"><Activity className="w-4 h-4 text-green-400" />{t('progress.weeklyDistribution','Weekly Focus Distribution')}</h3>
                    <span className={`text-xs ${theme.textSecondary}`}>{t('progress.last7Days','Last 7 days')}</span>
                  </div>
                  <div className="h-24">
                    <svg viewBox="0 0 160 40" className="w-full h-full overflow-visible">
                      <polyline fill="none" stroke="url(#gradFocus)" strokeWidth="3" strokeLinecap="round" points={sparkPoints} />
                      <defs>
                        <linearGradient id="gradFocus" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#6366f1" />
                          <stop offset="50%" stopColor="#8b5cf6" />
                          <stop offset="100%" stopColor="#ec4899" />
                        </linearGradient>
                      </defs>
                    </svg>
                  </div>
                  <div className="grid grid-cols-7 gap-2 text-center text-[10px] text-slate-400 font-mono">
                    {(advanced.weeklyFocus || []).map((m,i)=> <div key={i} className="flex flex-col"><span>{m}</span></div>)}
                  </div>
                  <div className="flex justify-between text-xs text-slate-400">
                    <span>{t('progress.sessions7d','Sessions (7d)')}: {advanced.sessionsLast7Days}</span>
                    <span>{t('progress.focusMinutesToday','Focus Today (min)')}: {advanced.focusMinutesToday}</span>
                  </div>
                </motion.div>

                {/* Category Breakdown */}
                <motion.div whileHover={{ scale:1.01 }} className={`${theme.card} rounded-2xl p-6 flex flex-col gap-4`}>
                  <h3 className="font-semibold flex items-center gap-2"><PieChart className="w-4 h-4 text-pink-400" />{t('progress.categoryBreakdown','Category Breakdown')}</h3>
                  <div className="space-y-2">
                    {(advanced.categoryBreakdown.slice(0,6)).map(cat => (
                      <div key={cat.category} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)' }}></span>
                          <span className="capitalize">{t(`digitalDiscipline.categories.${cat.category}`, cat.category)}</span>
                        </div>
                        <div className="flex items-center gap-3 font-mono">
                          <span>{cat.minutes}m</span>
                          <div className="w-24 bg-slate-500/20 rounded h-2 overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-indigo-500 to-fuchsia-500" style={{ width: `${cat.percent}%` }} />
                          </div>
                          <span className="text-xs text-slate-400">{cat.percent}%</span>
                        </div>
                      </div>
                    ))}
                    {advanced.categoryBreakdown.length === 0 && (
                      <p className={`text-xs ${theme.textSecondary}`}>{t('digitalDiscipline.stats.noDataYet','No data yet')}</p>
                    )}
                  </div>
                </motion.div>
              </div>

              {/* Achievements + Navigation */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <motion.div className={`${theme.card} rounded-2xl p-6 flex flex-col gap-3 lg:col-span-2`} whileHover={{scale:1.01}}>
                  <h3 className="font-semibold flex items-center gap-2"><Layers className="w-4 h-4 text-yellow-400" />{t('progress.achievements','Recent Achievements')}</h3>
                  <div className="flex flex-wrap gap-3">
                    {advanced.achievements.length === 0 && (
                      <span className={`text-xs ${theme.textSecondary}`}>{t('progress.noAchievements','No achievements yet')}</span>
                    )}
                    {advanced.achievements.map(a => (
                      <div key={a.id} className="px-3 py-2 rounded-xl bg-gradient-to-r from-indigo-500/20 to-purple-500/20 border border-indigo-500/30 text-xs font-medium flex items-center gap-2">
                        <span>🏅</span>
                        <span>{a.type}: {a.value}</span>
                      </div>
                    ))}
                  </div>
                  <div className="text-[10px] uppercase tracking-wide text-slate-400 mt-auto">{t('progress.updatedNow','Updated now')}</div>
                </motion.div>
                <motion.div className={`${theme.card} rounded-2xl p-6 flex flex-col justify-between`} whileHover={{scale:1.02}}>
                  <div>
                    <h3 className="font-semibold mb-1">{t('progress.viewFullStats','View Complete Statistics')}</h3>
                    <p className={`text-xs ${theme.textSecondary} mb-4`}>{t('progress.viewFullStatsDesc','Open the unified analytics screen')}</p>
                  </div>
                  <motion.button
                    whileHover={{ scale:1.04 }}
                    whileTap={{ scale:0.96 }}
                    onClick={() => navigateToScreen && navigateToScreen('istatistikler', { source: 'discipline-progress', tab: 'discipline' })}
                    className={`${theme.button} w-full text-white py-3 rounded-xl font-medium flex items-center justify-center gap-2`}
                  >
                    <BarChart2 className="w-5 h-5" />
                    {t('progress.openStats','Open Stats')}
                  </motion.button>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Modals */}
      <AnimatePresence>
        {showGoalModal && (
          <CreateDisciplineGoalModal
            isOpen={showGoalModal}
            onClose={() => setShowGoalModal(false)}
            onSubmit={handleCreateGoal}
            isDarkMode={isDarkMode}
          />
        )}
      </AnimatePresence>

      {/* Elegant Notification System */}
      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: -100, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -100, scale: 0.9 }}
            className="fixed top-4 right-4 z-50 max-w-sm"
          >
            <div className={`
              ${theme.card} rounded-2xl p-4 shadow-2xl border
              ${notification.type === 'success' && 'border-green-500/50 bg-green-50/10'}
              ${notification.type === 'error' && 'border-red-500/50 bg-red-50/10'}
              ${notification.type === 'warning' && 'border-yellow-500/50 bg-yellow-50/10'}
              ${notification.type === 'info' && 'border-blue-500/50 bg-blue-50/10'}
            `}>
              <div className="flex items-start space-x-3">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center
                  ${notification.type === 'success' && 'bg-green-500'}
                  ${notification.type === 'error' && 'bg-red-500'}
                  ${notification.type === 'warning' && 'bg-yellow-500'}
                  ${notification.type === 'info' && 'bg-blue-500'}
                `}>
                  {notification.type === 'success' && <CheckCircle className="w-4 h-4 text-white" />}
                  {notification.type === 'error' && <X className="w-4 h-4 text-white" />}
                  {notification.type === 'warning' && <Clock className="w-4 h-4 text-white" />}
                  {notification.type === 'info' && <Sparkles className="w-4 h-4 text-white" />}
                </div>
                <p className={`${theme.text} text-sm font-medium flex-1`}>
                  {notification.message}
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ModernDisciplinePanel;
